/**
 * Image Compression utilities — client-side image compression using Canvas API.
 *
 * Compresses images to a target max size and dimensions before uploading.
 * No server or external library required — uses browser Canvas API.
 *
 * Usage:
 *   import { compressImage } from './compressionUtils';
 *
 *   const compressed = await compressImage(file, {
 *     maxWidth: 2000,
 *     maxHeight: 2000,
 *     quality: 0.85,
 *     maxSizeKB: 1024,  // max 1MB
 *   });
 *   // compressed is a File object
 */

/**
 * Compress an image File to the specified dimensions and quality.
 *
 * @param {File} file — input image file
 * @param {Object} options
 * @param {number} [options.maxWidth] — max width in pixels (default: 2000)
 * @param {number} [options.maxHeight] — max height in pixels (default: 2000)
 * @param {number} [options.quality] — JPEG/WebP quality 0-1 (default: 0.85)
 * @param {number} [options.maxSizeKB] — max output size in KB (default: 1024)
 * @param {string} [options.outputFormat] — 'image/jpeg' | 'image/webp' | 'image/png' (default: 'image/jpeg')
 * @returns {Promise<File>} — compressed File
 */
export const compressImage = (file, options = {}) => {
  const {
    maxWidth = 2000,
    maxHeight = 2000,
    quality = 0.85,
    maxSizeKB = 1024,
    outputFormat = 'image/jpeg',
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        // Scale down to fit within maxWidth/maxHeight
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Try reducing quality until we're under maxSizeKB
        let q = quality;
        const tryCompress = () => {
          canvas.toBlob(
            blob => {
              if (blob.size > maxSizeKB * 1024 && q > 0.3) {
                q -= 0.1;
                tryCompress();
                return;
              }
              const compressed = new File([blob], file.name, {
                type: outputFormat,
                lastModified: Date.now(),
              });
              resolve(compressed);
            },
            outputFormat,
            q
          );
        };
        tryCompress();
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Get image dimensions without loading fully.
 * @param {File} file
 * @returns {Promise<{width: number, height: number}>}
 */
export const getImageDimensions = file => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Check if a file needs compression.
 * @param {File} file
 * @param {number} maxSizeKB
 * @returns {boolean}
 */
export const needsCompression = (file, maxSizeKB = 1024) =>
  file && file.size > maxSizeKB * 1024;
